import {
  faFloppyDisk,
  faMinus,
  faPlus,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  InputBase,
  MenuItem,
  Select,
  styled,
  SxProps,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import Q3DIconButton from "./Q3DIconButton";
import { TQ3DSelectItem } from "./Q3DSelect";
import { EMPTY } from "../config/componentsConfig";
import { ReactNode, useContext, useEffect, useState } from "react";
import { EDisplayType, ProjectDetailContext } from "../../pages/ProjectDetail";
import FormatUtil from "../Utility/ProjectComUtil";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "0px",
  lineHeight: "1.0",
  minWidth: "60px",
  [`&.${tableCellClasses.head}`]: {
    borderBottom: `1px solid ${theme.palette.base.contrast}`,
    fontSize: 14,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  [theme.breakpoints.down("sm")]: {
    paddingRight: "0px !important",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

type IconCellProps = {
  icon: IconDefinition;
  color: string;
  onClick?: () => void;
  disabled?: boolean;
  sx?: SxProps;
};

const IconTableCell = (props: IconCellProps) => {
  return (
    <StyledTableCell>
      <Q3DIconButton
        icon={
          <FontAwesomeIcon
            icon={props.icon}
            color={props.color}
            fontSize="26px"
          />
        }
        onClick={() => props.onClick()}
        disabled={props.disabled}
        disabledBorder={true}
      />
    </StyledTableCell>
  );
};

type SelectCellProps = {
  value: string;
  items: TQ3DSelectItem[];
  onChange?: (v: string) => void;
  sx?: SxProps;
};

const SelectTableCell = (props: SelectCellProps) => {
  return (
    <StyledTableCell>
      <Select
        variant="standard"
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        value={props.value}
        sx={{
          height: "28px",
          width: "100%",
          borderRadius: "0px",
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: "200px",
            },
          },
        }}
      >
        {props.items?.map((item) => (
          <MenuItem value={item.value} sx={{ whiteSpace: "break-spaces" }}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </StyledTableCell>
  );
};

type TextCellProps = {
  value: string;
  onChange?: (v: string) => void;
  sx?: SxProps;
};

const TextTableCell = (props: TextCellProps) => {
  return (
    <StyledTableCell>
      <InputBase
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
      />
    </StyledTableCell>
  );
};

type NumberCellProps = {
  value: number;
  onChange?: (v: number) => void;
  disabled?: boolean;
  sx?: SxProps;
};

const NumberTableCell = (props: NumberCellProps) => {
  const [isFixed, setIsFixed] = useState<boolean>(true);
  const theme = useTheme();
  const { displayType } = useContext(ProjectDetailContext);

  return (
    <StyledTableCell>
      <InputBase
        type="number"
        value={isFixed ? props.value?.toFixed(3) : props.value}
        onChange={(e) => {
          setIsFixed(false);
          props.onChange(Number(e.target.value));
        }}
        onBlur={(e) => {
          setIsFixed(true);
          props.onChange(Number(e.target.value));
        }}
        disabled={props.disabled}
        inputProps={{
          style: {
            textAlign: "right",
            paddingRight: displayType === EDisplayType.iOS ? "0" : "4px",
          },
        }}
        sx={{
          width: "100%",
          textAlign: "right",
          paddingRight: "0px",
          "input::-webkit-inner-spin-button": {
            transform: "translateX(4px)",
          },
          "input::-webkit-outer-spin-button": {
            transform: "translateX(4px)",
          },
          ".Mui-disabled": {
            WebkitTextFillColor: `${theme.palette.base.contrast} !important`,
          },
        }}
      />
    </StyledTableCell>
  );
};

type DateCellProps = {
  value: Date;
  sx?: SxProps;
};

const DateTableCell = (props: DateCellProps) => {
  const { isJapanese } = useContext(ProjectDetailContext);
  return (
    <StyledTableCell>
      {FormatUtil.valFormat(props.value, isJapanese)}
    </StyledTableCell>
  );
};

export type Q3DDataTableHeaderType = {
  key: string;
  name: string;
  type: Q3DCellType;
  rightIcon?: ReactNode;
  sx?: SxProps;
};

export type Q3DCellType =
  | "default"
  | "icon"
  | "select"
  | "text"
  | "number"
  | "date"
  | "textInput"
  | "numberInput";

type Q3DCellProps = {
  type: Q3DCellType;
  value: any;
  options?: IconCellProps | TextCellProps | NumberCellProps;
  selectItems?: TQ3DSelectItem[];
  onClick?: () => void;
  onChange?: (value: any) => void;
  sx?: SxProps;
};

export const Q3DTableCell = (props: Q3DCellProps) => {
  switch (props.type) {
    case "icon":
      return (
        <IconTableCell
          icon={(props.options as IconCellProps).icon}
          color={(props.options as IconCellProps).color}
          disabled={(props.options as IconCellProps).disabled}
          onClick={() => {
            props.onClick();
          }}
        />
      );
    case "select":
      return (
        <SelectTableCell
          value={props.value}
          items={props.selectItems}
          onChange={(v) => {
            props.onChange(v);
          }}
          sx={props.sx}
        />
      );
    case "textInput":
      return (
        <TextTableCell
          value={props.value as string}
          onChange={(v) => {
            props.onChange(v);
          }}
          sx={props.sx}
        />
      );
    case "numberInput":
      return (
        <NumberTableCell
          value={props.value as number}
          onChange={(v) => {
            props.onChange(v);
          }}
          sx={props.sx}
        />
      );
    case "date":
      return <DateTableCell value={props.value as Date} sx={props.sx} />;
    case "text":
      return <StyledTableCell sx={props.sx}>{props.value}</StyledTableCell>;
    case "number":
      return (
        <NumberTableCell
          value={props.value as number}
          onChange={(v) => {
            props.onChange(v);
          }}
          disabled={true}
          sx={props.sx}
        />
      );
    case "default":
      return <StyledTableCell>{props.value}</StyledTableCell>;
  }
};

type Props = {
  headers: Q3DDataTableHeaderType[];
  rows: any[];
  selectItems?: TQ3DSelectItem[];
  onChange?: (id: string, key: string, value: any) => void;
  onClickHeaderButton?: () => void;
  onClickRowButton?: (id: string, isSave?: boolean) => void;
  onClickBottomButton?: () => void;
  ContainerProps?: { sx?: SxProps };
};

export const Q3DDataTable = (props: Props) => {
  const [hasEMPTY, setHasEMPTY] = useState<boolean>(false);
  const [showTool, setShowTool] = useState<boolean>(false);

  useEffect(() => {
    props.headers.forEach((header) => {
      if (header.type === "icon") {
        setShowTool(true);
      }
    });
  }, []);

  useEffect(() => {
    let isSave = false;
    props.rows?.map((row) => {
      if (row.name === EMPTY) {
        isSave = true;
      }
    });
    setHasEMPTY(isSave);
  }, [props.rows]);

  return (
    <TableContainer sx={{ ...props.ContainerProps?.sx }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {props.headers?.map((header) => {
              if (header.type === "icon") {
                return (
                  <IconTableCell
                    icon={faMinus}
                    color={"red"}
                    onClick={() => {
                      props.onClickHeaderButton();
                    }}
                  />
                );
              } else {
                return (
                  <StyledTableCell sx={header.sx}>
                    {header.name} {header.rightIcon}
                  </StyledTableCell>
                );
              }
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.rows?.map((row, index) => (
            <StyledTableRow key={row.id}>
              {props.headers?.map((header) => (
                <Q3DTableCell
                  type={header.type}
                  value={row[header.key]}
                  options={
                    header.type === "icon" &&
                    row.name === EMPTY &&
                    index === props.rows.length - 1
                      ? { icon: faFloppyDisk, color: "blue" }
                      : { icon: faMinus, color: "red" }
                  }
                  selectItems={props.selectItems}
                  sx={header.sx}
                  onClick={() => {
                    const isSave =
                      header.type === "icon" &&
                      row.name === EMPTY &&
                      index === props.rows.length - 1;
                    props.onClickRowButton(row.id, isSave);
                  }}
                  onChange={(value) => {
                    props.onChange(row.id, header.key, value);
                  }}
                />
              ))}
            </StyledTableRow>
          ))}
          {showTool && !hasEMPTY && (
            <StyledTableRow key={"#"}>
              {props.headers.map((header) => {
                if (header.type === "icon")
                  return (
                    <Q3DTableCell
                      type={header.type}
                      value={""}
                      options={{
                        icon: faPlus,
                        color: "blue",
                        disabled: hasEMPTY,
                      }}
                      onClick={() => {
                        props.onClickBottomButton();
                      }}
                    />
                  );
                else return <StyledTableCell></StyledTableCell>;
              })}
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
